import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { oauthCallback, personalOauthCallback } from '../lib/api';
import { Logo } from '../components/Logo';
import { useUserStore, scopedStorageKey } from '../store/userStore';
import { queryKeys } from '../lib/queryKeys';

const GOOGLE_ONBOARDING_KEY_PREFIX = 'octopm-google-onboarding';

export function OAuthCallback({ personal = false }: { personal?: boolean }) {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const called = useRef(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Authorization denied: ${errorParam}`);
      return;
    }

    if (!provider || !code) {
      setError('Invalid callback — missing provider or authorization code.');
      return;
    }

    // Wait for Firebase to rehydrate the session before making authenticated request
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (!user) {
        const redirectBase = personal
          ? `/integrations/personal/${provider}/callback`
          : `/integrations/${provider}/callback`;
        navigate(`/login?redirect=${redirectBase}&code=${encodeURIComponent(code)}&state=${encodeURIComponent(state ?? '')}`, { replace: true });
        return;
      }
      const callbackFn = personal
        ? personalOauthCallback(provider, code, state ?? '')
        : oauthCallback(provider, code, state ?? '');
      callbackFn
        .then((result) => {
          // Health is now written eagerly by the backend on a verified
          // reconnect (see backend/app/routers/integrations.py), but the
          // Settings page may already have a cached health query in
          // flight — invalidate so the badge reflects the true state
          // immediately rather than waiting for the next 60s poll.
          void qc.invalidateQueries({ queryKey: queryKeys.integrationHealth });
          void qc.invalidateQueries({ queryKey: queryKeys.integrations });
          if (personal) {
            // Check if this came from the onboarding modal. Read via
            // getState() rather than a reactive hook value: this effect
            // only runs once (guarded by `called`), so a hook-subscribed
            // value would be frozen at whatever sessionConfig was on the
            // very first render — almost always still null this early in
            // the auth flow. getState() always reads the current value.
            const { sessionConfig } = useUserStore.getState();
            const key = scopedStorageKey(GOOGLE_ONBOARDING_KEY_PREFIX, sessionConfig?.entityId, sessionConfig?.userId);
            const fromOnboarding = key ? localStorage.getItem(key) : null;
            if (key) localStorage.removeItem(key);
            if (fromOnboarding) {
              navigate('/?google_connected=1', { replace: true });
              return;
            }
          }
          // A workspace reconnect can complete the OAuth handshake yet
          // still fail to produce a working credential (see
          // backend/app/routers/integrations.py) — e.g. the provider
          // short-circuited its consent screen without issuing a fresh
          // token. Surface that distinctly rather than letting the flow
          // read as an ordinary successful connect.
          const verifyFailed =
            !personal && 'healthVerified' in result && result.healthVerified === false;
          const verifyParam = verifyFailed ? '&reconnectVerified=0' : '';
          navigate(`/settings?tab=integrations&connected=${provider}${verifyParam}`, { replace: true });
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Connection failed.';
          setError(msg);
        });
    });

    return () => unsubscribe();
  }, [provider, personal, searchParams, navigate, qc]);

  return (
    <div className="min-h-screen bg-white dark:bg-bg-deep flex items-center justify-center p-8">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8">
          <Logo className="h-10 w-auto text-gray-900 dark:text-white" />
        </div>

        {error ? (
          <>
            <h1 className="text-title font-bold text-gray-900 dark:text-white mb-3">
              Connection failed
            </h1>
            <p className="text-meta text-red-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/settings?tab=integrations', { replace: true })}
              className="px-4 py-2 bg-gray-800 dark:bg-primary text-white rounded-full text-meta font-medium hover:opacity-90 transition-opacity"
            >
              Back to Settings
            </button>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-meta text-gray-500 dark:text-gray-400">
              Completing connection…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
